'use client';

import { useRef } from 'react';

import type { FormDirtyContextValue } from '../declarative-form/FormDirtyContext';

export interface UseFormDirtyConfirmResult {
  contextValue: FormDirtyContextValue;
  confirmBeforeClose: () => boolean;
}

interface FormDirtyState {
  isDirty: boolean;
  isSubmitted: boolean;
}

export function useFormDirtyConfirm(): UseFormDirtyConfirmResult {
  // SheetWrapper evaluates the guard lazily, so ref updates avoid rerendering the sheet on every form change.
  const formStatesRef = useRef(new Map<string, FormDirtyState>());
  const contextValueRef = useRef<FormDirtyContextValue>({
    reportDirty(formId, isDirty) {
      const currentState = formStatesRef.current.get(formId);
      formStatesRef.current.set(formId, {
        isDirty,
        isSubmitted: isDirty ? false : (currentState?.isSubmitted ?? false),
      });
    },
    reportSubmitted(formId) {
      const currentState = formStatesRef.current.get(formId);
      formStatesRef.current.set(formId, {
        isDirty: currentState?.isDirty ?? false,
        isSubmitted: true,
      });
    },
  });
  const confirmBeforeCloseRef = useRef(() => {
    return Array.from(formStatesRef.current.values()).some(({ isDirty, isSubmitted }) => isDirty && !isSubmitted);
  });

  return {
    contextValue: contextValueRef.current,
    confirmBeforeClose: confirmBeforeCloseRef.current,
  };
}
